import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BucketEditor } from '@/components/semantic-models/BucketEditor';
import { Layers, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/**
 * BinFieldDialog — inline "Bin this field" affordance surfaced from the
 * report builder. Creates a new bucketed calculated dimension on the
 * source field's entity so the field is instantly available for use in
 * charts. No changes to the report itself — the user has to drag the
 * new field onto the axis afterward.
 *
 * Props:
 *   open, onOpenChange
 *   field: { name, display_name?, entity_id, entity_name, data_type }
 *   modelId
 *   onCreated?: (attribute) => void   — parent should append to allFields
 */
export function BinFieldDialog({ open, onOpenChange, field, modelId, onCreated }) {
  const [name, setName] = useState('');
  const [bucketSpec, setBucketSpec] = useState(null);
  const [formula, setFormula] = useState('');
  const [sortExpr, setSortExpr] = useState('');
  const [valid, setValid] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open && field) {
      setName(`${(field.display_name || field.name).replace(/\s+/g, ' ')} bucket`);
      setBucketSpec(null);
      setFormula('');
      setValid(false);
    }
  }, [open, field]);

  if (!field) return null;

  const isDate = (field.data_type || '').toLowerCase().includes('date');
  const isNumeric = ['integer', 'float', 'number'].includes((field.data_type || '').toLowerCase()) || field.semantic_type === 'measure';
  const isText   = ['string', 'text', 'varchar', 'character'].includes((field.data_type || '').toLowerCase());
  const fieldsMock = [field];

  const save = async () => {
    if (!valid || !name.trim() || !formula || !field.entity_id) {
      toast.error('Give the field a name and finish defining at least one bucket.');
      return;
    }
    setSaving(true);
    try {
      // Re-fetch fresh attributes to avoid clobbering concurrent edits.
      const model = await api.getSemanticModelFull(modelId);
      const ent = (model.entities || []).find(e => e.entity_id === field.entity_id) || { attributes: [] };
      const newAttr = {
        name: name.replace(/\s+/g, '_').toLowerCase(),
        display_name: name,
        data_type: 'string',
        semantic_type: 'dimension',
        description: `Bucketed: ${field.name}`,
        formula,
        subtype: 'bucket',
        bucket_spec: bucketSpec,
        sort_expr: sortExpr || null,
        sort_field: null,
        is_calculated: true,
        visible: true,
      };
      await api.updateSemanticEntity(field.entity_id, {
        attributes: [...(ent.attributes || []), newAttr],
      });
      toast.success(`Created "${name}" — drag it onto a chart to use it.`);
      onCreated && onCreated({ ...newAttr, entity_id: field.entity_id, entity_name: field.entity_name });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Failed to create bucketed field');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bin-field-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-500" /> Bin field: {field.display_name || field.name}
          </DialogTitle>
          <DialogDescription>
            Turn <code className="text-xs text-primary">{field.name}</code> into a bucketed dimension. Great for aging brackets, tiers, or grouping continuous values.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[10px] text-muted-foreground">New field name</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              className="mt-0.5 h-8 text-sm"
              placeholder={`${field.name}_bucket`}
              data-testid="bin-field-name"
            />
          </div>

          <BucketEditor
            modelId={modelId}
            entityName={field.entity_name}
            defaultBaseField={field.name}
            fields={fieldsMock}
            fieldsNumeric={isNumeric ? fieldsMock : []}
            fieldsDate={isDate ? fieldsMock : []}
            fieldsText={isText ? fieldsMock : []}
            testIdPrefix="bin-field-bucket"
            onChange={({ spec, valid: v, formula: fx, sortExpr: sx }) => {
              setBucketSpec(spec);
              setValid(v);
              if (v) { setFormula(fx || ''); setSortExpr(sx || ''); }
              else   { setFormula(''); setSortExpr(''); }
            }}
          />

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="bin-field-cancel">Cancel</Button>
            <Button onClick={save} disabled={!valid || !name.trim() || saving} className="flex-1" data-testid="bin-field-save">
              <Sparkles className="w-4 h-4 mr-1.5" />
              {saving ? 'Saving…' : 'Create bucketed field'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
